/**
 * Custom License Validator
 * Validates self-issued/custom license keys locally
 * Custom keys format: CUSTOM-{purpose}-{userId}-{expiryTimestamp}-{signature}
 * Examples:
 *   - CUSTOM-PROMO-USER001-20251231-ABC123 (promotional license)
 *   - CUSTOM-SPONSOR-ORG001-PERMANENT-XYZ789 (permanent sponsor license)
 */

import type {
  LicenseInfo,
  LicenseValidationResult,
  LicenseActivationResult,
  OfflineGracePeriodStatus,
  DeviceInfo,
  LicenseValidationError
} from '$lib/types/subscription';

import type {
  LicenseValidator,
  SecureLicenseStorage,
  DeviceLimitManager,
  OfflineGracePeriodManager,
  LicenseProgressCallback
} from './interfaces';

import { LICENSE_CONFIG } from '$lib/constants/subscription';

/**
 * Custom License Validator
 * Handles local validation of custom/self-issued licenses
 */
export class CustomLicenseValidator implements LicenseValidator {
  private readonly storage: SecureLicenseStorage;
  private readonly deviceManager: DeviceLimitManager;
  private readonly gracePeriodManager: OfflineGracePeriodManager;

  constructor(
    storage: SecureLicenseStorage,
    deviceManager: DeviceLimitManager,
    gracePeriodManager: OfflineGracePeriodManager
  ) {
    this.storage = storage;
    this.deviceManager = deviceManager;
    this.gracePeriodManager = gracePeriodManager;
  }

  /**
   * Check if a license key is a custom key
   */
  static isCustomKey(licenseKey: string): boolean {
    return licenseKey.startsWith('CUSTOM-');
  }

  /**
   * Validate a custom license key
   */
  async validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
    try {
      // Check if it's a custom key
      if (!CustomLicenseValidator.isCustomKey(licenseKey)) {
        return this.createErrorResult('invalid_format', 'Not a custom license key');
      }

      // Parse the custom key
      const parsed = this.parseCustomKey(licenseKey);
      if (!parsed.valid) {
        return this.createErrorResult('invalid_format', parsed.error || 'Invalid custom key format');
      }

      // Check if expired
      if (parsed.expiresAt && new Date() > parsed.expiresAt) {
        return this.createErrorResult('expired_license', 'Custom license has expired');
      }

      // Create license info
      const currentDevice = await this.deviceManager.getCurrentDeviceInfo();
      const licenseInfo: LicenseInfo = {
        licenseKey,
        provider: 'manual',
        productId: `custom-${parsed.purpose}`,
        productName: `Korean Grammar Checker Custom (${parsed.purpose})`,
        email: parsed.userId,
        purchaseDate: new Date(), // Custom license issue date
        expiresAt: parsed.expiresAt,
        uses: 0,
        maxUses: undefined,
        devices: [currentDevice],
        maxDevices: LICENSE_CONFIG.MAX_DEVICES,
        isActive: true,
        lastValidatedAt: new Date(),
        metadata: {
          type: 'custom',
          purpose: parsed.purpose,
          userId: parsed.userId,
          isPermanent: parsed.isPermanent
        }
      };

      // Store license
      await this.storage.storeLicense(licenseKey, licenseInfo);

      return {
        isValid: true,
        license: licenseInfo,
        fromCache: false,
        validatedAt: new Date()
      };
    } catch (error) {
      return this.createErrorResult('validation_failed', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Parse custom key format: CUSTOM-{purpose}-{userId}-{expiryTimestamp|PERMANENT}-{signature}
   */
  private parseCustomKey(licenseKey: string): {
    valid: boolean;
    purpose?: string;
    userId?: string;
    expiresAt?: Date;
    isPermanent?: boolean;
    signature?: string;
    error?: string;
  } {
    try {
      const parts = licenseKey.split('-');

      // Must have at least 4 parts: CUSTOM, purpose, userId, signature
      if (parts.length < 4) {
        return { valid: false, error: 'Invalid custom key format' };
      }

      const [prefix, purpose, userId, expiryOrPermanent, signature] = parts;

      if (prefix !== 'CUSTOM') {
        return { valid: false, error: 'Invalid prefix' };
      }

      if (!purpose) {
        return { valid: false, error: 'Missing purpose' };
      }

      if (!userId) {
        return { valid: false, error: 'Missing user ID' };
      }

      // Parse expiry or permanent
      let expiresAt: Date | undefined;
      let isPermanent = false;

      if (expiryOrPermanent === 'PERMANENT') {
        isPermanent = true;
      } else {
        // Try to parse as Unix timestamp or YYYYMMDD
        if (expiryOrPermanent.length === 8) {
          // YYYYMMDD format
          const year = parseInt(expiryOrPermanent.substring(0, 4));
          const month = parseInt(expiryOrPermanent.substring(4, 6)) - 1;
          const day = parseInt(expiryOrPermanent.substring(6, 8));
          expiresAt = new Date(year, month, day);
        } else {
          // Try Unix timestamp
          const timestamp = parseInt(expiryOrPermanent);
          if (!isNaN(timestamp)) {
            expiresAt = new Date(timestamp * 1000);
          }
        }
      }

      // Validate signature format (alphanumeric, at least 6 chars)
      if (!signature || signature.length < 6 || !/^[A-Z0-9]+$/i.test(signature)) {
        return { valid: false, error: 'Invalid signature format' };
      }

      return {
        valid: true,
        purpose,
        userId,
        expiresAt,
        isPermanent,
        signature
      };
    } catch (error) {
      return { valid: false, error: 'Failed to parse custom key' };
    }
  }

  /**
   * Activate custom license on device
   */
  async activateLicense(
    licenseKey: string,
    deviceInfo: DeviceInfo,
    progressCallback?: LicenseProgressCallback
  ): Promise<LicenseActivationResult> {
    try {
      progressCallback?.({ stage: 'validation', message: 'Validating custom license...', progress: 10 });

      // Validate the license
      const validationResult = await this.validateLicense(licenseKey);
      if (!validationResult.isValid || !validationResult.license) {
        return {
          success: false,
          error: validationResult.error || this.createError('invalid_license', 'Custom license validation failed')
        };
      }

      progressCallback?.({ stage: 'validation', message: 'Custom license validated', progress: 30 });

      // Check device limits
      progressCallback?.({ stage: 'activation', message: 'Checking device limits...', progress: 50 });

      const canActivate = await this.canActivateOnDevice(licenseKey, deviceInfo);
      if (!canActivate) {
        return {
          success: false,
          requiresDeviceDeactivation: true,
          error: this.createError('device_limit_exceeded', 'Maximum device limit reached'),
          deviceInfo
        };
      }

      // Add device
      progressCallback?.({ stage: 'activation', message: 'Activating on device...', progress: 70 });

      const deviceAdded = await this.deviceManager.addDevice(licenseKey, deviceInfo);
      if (!deviceAdded) {
        return {
          success: false,
          error: this.createError('validation_failed', 'Failed to add device')
        };
      }

      // Store license
      progressCallback?.({ stage: 'storage', message: 'Storing license data...', progress: 90 });
      await this.storage.storeLicense(licenseKey, validationResult.license);

      progressCallback?.({ stage: 'cleanup', message: 'Activation complete', progress: 100 });

      return {
        success: true,
        license: validationResult.license,
        deviceInfo
      };
    } catch (error) {
      return {
        success: false,
        error: this.createError('unknown_error', error instanceof Error ? error.message : 'Activation failed')
      };
    }
  }

  /**
   * Deactivate custom license on device
   */
  async deactivateLicense(licenseKey: string, deviceId?: string): Promise<boolean> {
    try {
      const targetDeviceId = deviceId || (await this.deviceManager.getCurrentDeviceInfo()).deviceId;

      // Remove from storage
      await this.storage.removeLicense(licenseKey);
      await this.deviceManager.removeDevice(licenseKey, targetDeviceId);

      return true;
    } catch (error) {
      console.error('Failed to deactivate custom license:', error);
      return false;
    }
  }

  /**
   * Check if custom license can be validated offline (always true for custom)
   */
  async canValidateOffline(licenseKey: string): Promise<boolean> {
    return await this.storage.hasLicense(licenseKey);
  }

  /**
   * Get offline grace period status (custom licenses don't need grace period)
   */
  async getOfflineGracePeriodStatus(licenseKey: string): Promise<OfflineGracePeriodStatus> {
    // Custom licenses are always valid offline until expiry
    return {
      isInGracePeriod: true,
      lastValidationAt: new Date(),
      gracePeriodExpiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year
      daysRemaining: 365
    };
  }

  /**
   * Refresh custom license cache
   */
  async refreshLicenseCache(licenseKey: string): Promise<LicenseValidationResult> {
    // Re-validate the license
    return await this.validateLicense(licenseKey);
  }

  /**
   * Get device information for custom license
   */
  async getDeviceInfo(licenseKey: string): Promise<DeviceInfo[]> {
    return await this.deviceManager.getActiveDevices(licenseKey);
  }

  /**
   * Check if device can be activated
   */
  async canActivateOnDevice(licenseKey: string, deviceInfo: DeviceInfo): Promise<boolean> {
    const cachedLicense = await this.storage.getLicense(licenseKey);
    if (!cachedLicense) {
      // First activation - always allow
      return true;
    }

    return await this.deviceManager.canAddDevice(licenseKey, cachedLicense.maxDevices);
  }

  /**
   * Helper methods
   */

  private createErrorResult(type: string, message: string): LicenseValidationResult {
    return {
      isValid: false,
      error: this.createError(type as any, message),
      validatedAt: new Date()
    };
  }

  private createError(type: string, message: string): LicenseValidationError {
    return {
      type: type as any,
      message,
      retryable: false,
      details: {}
    };
  }
}